<script setup lang="ts">
import { equipCollection } from '@/core/equip'
import { useUserStore } from '@/stores/modules/user'
import type { EquipItemType } from '@/types/equip'
import { computed, ref } from 'vue'
import { equipIcon, equipTypeList } from '../../item/data'
import { getEngraveDataKey } from '@/core/equip/dark-gold'
import { socketEmitAsync } from '@/utils/web-socket'
import type { PurityRes } from '@/types/smithy'
import { formatNumberWithCommas } from '@/utils'
import { ElMessage } from 'element-plus'
import { getEquipName } from '@/utils/equip'
import { QuestionFilled } from '@element-plus/icons-vue'
import { explainDarkGoldEquipAffix } from '@/core/equip/dark-gold'

const userStore = useUserStore()

const equipItemId = ref('')
const equipInfo = computed(() => {
  const res = userStore.itemList.find((item) => item.id === equipItemId.value)
  if (!res) {
    equipItemId.value = ''
    return undefined
  }
  const { name, type, attrs } = equipCollection[res.equipId]
  return { ...res, name, type, attrs }
})
const equippedList = computed(() =>
  equipTypeList.value.map((item) => {
    const itemId = userStore.equippedList[item]
    const equipInfo = userStore.itemList.find((item) => item.id === itemId)
    if (!equipInfo) return undefined
    const { name, type, attrs } = equipCollection[equipInfo.equipId]
    return { ...equipInfo, name, type, attrs }
  }),
)

const itemList = computed<EquipItemType[]>(() => {
  const items = userStore.itemList
  if (userStore.itemList.length === 0) return []
  return items
    .filter((item) => {
      const equippedIdList = Object.values(userStore.equippedList)
      if (!item.darkGoldAttrs) return false
      return !equippedIdList.includes(item.id)
    })
    .map((item) => {
      const { equipId } = item
      const { name, type, attrs } = equipCollection[equipId]
      return { ...item, name, type, attrs }
    })
})

function handleChoice(item: EquipItemType | undefined) {
  if (item && !item.engraveAttr) return ElMessage.warning('仅已刻印装备可提纯')
  equipItemId.value = item?.id || ''
}

const isPurifyMax = computed(() => {
  if (!equipInfo.value) return false
  const data = equipInfo.value.engraveAttr?.special[0].data
  if (data.value === 10 || data.multiplier === 10 || data.rate === 10) return true
  return false
})

function handlePurify() {
  if (!equipInfo.value) return
  const { id } = equipInfo.value
  if (userStore.diamond < 500) return ElMessage.warning('您的钻石不足')
  if (userStore.consumables.purifyStone < 1) return ElMessage.warning('您的提纯石不足')

  ElMessageBox.confirm(`<p class="affix">确定提纯【<span class="${equipInfo.value.quality}">${equipInfo.value.name}</span>】吗？</p>`, '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      const { value, userAttr } = await socketEmitAsync<PurityRes>({ type: 'purifyEquip', data: { id } })
      userStore.userAttr = userAttr

      const item = userStore.itemList.find((item) => item.id === id)
      if (!item || !equipInfo.value || !item.engraveAttr || !equipInfo.value.engraveAttr) return
      const dataKey = getEngraveDataKey(item.engraveAttr!.special)
      if (!dataKey) return
      item.engraveAttr.special[0].data[dataKey] = value

      userStore.diamond -= 500
      userStore.consumables.purifyStone -= 1
    })
    .catch(() => {})
}
</script>

<template>
  <div class="enchant">
    <div class="enchant-table border-wrap">
      <h4>锻造台</h4>
      <div class="enchant-equip">
        <CommonBtn style="width: 100%" @done="handleChoice(undefined)">
          <span v-if="equipInfo" v-html="getEquipName(equipInfo, equipInfo.type)"></span>
          <span v-else>(未放入)</span>
        </CommonBtn>
      </div>
      <div class="help-info">
        <el-popover placement="bottom" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>提纯说明</h4>
            <p>1. <span class="info">已刻印</span>的暗金装备可进行提纯。</p>
            <p>2. 提纯后将随机提升<span class="grow">0.2%</span> ~ <span class="grow">0.5%</span>的刻印属性数值。</p>
            <p>3. 提纯需消耗<span class="info">提纯石</span>，于<span class="hp">魔龙山巅</span>挑战中获取。</p>
          </div>
        </el-popover>
      </div>
      <template v-if="equipInfo">
        <div class="info-card">
          <template v-if="equipInfo.engraveAttr">
            <p style="display: block">
              刻印词条：
              <span class="affix" v-for="item of explainDarkGoldEquipAffix(equipInfo.engraveAttr.special, false)" v-html="item"></span>
            </p>
          </template>
          <p>提纯花费：500 钻石</p>
          <p style="text-indent: 5em">1 提纯石</p>
        </div>
        <div class="btn-wrap">
          <el-button type="warning" plain @click="handlePurify" :disabled="isPurifyMax">
            {{ isPurifyMax ? '已达提纯上限' : '开始提纯' }}
          </el-button>
        </div>
      </template>
    </div>
    <div class="item-list border-wrap" style="flex-shrink: 0; margin-bottom: 10px">
      <h4>已装备</h4>
      <div class="item-btn-wrap btn-wrap">
        <el-row :gutter="10">
          <el-col :span="12" v-for="(item, index) of equippedList" :key="index">
            <CommonBtn
              size="small"
              @done="handleChoice(item)"
              :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }"
              :type="item ? 'primary' : 'info'"
            >
              <span v-if="item" v-html="getEquipName(item, index)"></span>
              <span v-else>{{ equipIcon[equipTypeList[index]] }} (未装备)</span>
            </CommonBtn>
          </el-col>
        </el-row>
      </div>
    </div>
    <div class="item-list border-wrap package">
      <h4>背包</h4>
      <p class="cur-gold">
        <span>💎 {{ formatNumberWithCommas(userStore.diamond) }}</span>
        <span style="margin-left: 10px">⚪ {{ formatNumberWithCommas(userStore.consumables.purifyStone) }}</span>
      </p>
      <div class="item-btn-wrap btn-wrap item-list-wrap">
        <el-row :gutter="10">
          <el-col :span="12" v-for="item of itemList" :key="item.id">
            <CommonBtn size="small" @done="handleChoice(item)" :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }">
              <span v-html="getEquipName(item, item.type)"></span>
            </CommonBtn>
          </el-col>
        </el-row>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.enchant {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-flow: column nowrap;
  .enchant-table {
    position: relative;
    padding: 0 8px 10px 8px;
    margin-bottom: 10px;
    overflow: auto;
    .enchant-equip {
      border: 1px var(--fontColor) solid;
      padding: 6px 8px;
      border-radius: 4px;
      background-color: rgba($color: var(--fontColor), $alpha: 0.1);
    }
    .help-info {
      position: absolute;
      top: 5px;
      right: 10px;
    }
  }
  .item-list {
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    justify-content: unset;
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
    .cur-gold {
      width: 100%;
      text-align: right;
      font-size: 14px;
      padding-right: 10px;
      margin: 0;
    }
    .item-btn-wrap {
      overflow: auto;
    }
    &.package {
      flex: 1;
      min-height: 135px;
    }
  }
  .btn-wrap {
    & > div {
      justify-content: unset;
      margin-bottom: 0;
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
  }
}
</style>
